/**
 * Utility functions for template management and manipulation
 */

// Template data structure
export function getTemplates() {
  return [
    {
      id: 'almeria',
      title: 'Almeria',
      color: '#2DD0D6', 
      data: {
        regimen_solicitud: '10',
        declaracion_1: 'EX',
        declaracion_2: 'A',
        naturaleza_transaccion: '11',
        telefono: '948403026',
        contacto: 'ADUANAS@GDAR.ES',
        medio_transporte_frontera: '30',
        embalajes: [
          {
            marcas: [{ marca: 'RTDAS' }],
          },
        ],
      },
    },
    {
      id: 'iberveg',
      title: 'IBERVEG',
      color: '#10B981', 
      data: {
        aduana_despacho: '0441',
        pais_exportacion: 'ES',
        provincia: 'AL',
        localizacion_mercancia: '0441LAME05',
        documentos: [
          { codigo_documento: 'N380', tipo: 2 },
          { codigo_documento: 'N730', tipo: 4 },
          { codigo_documento: 'Y024', tipo: 3 },
          { codigo_documento: 'I5007', tipo: 2 },
          { codigo_documento: 'N003', tipo: 2 },
          { codigo_documento: '1217', tipo: 2 },
          { codigo_documento: '1009', tipo: 2 },
        ],
      },
    },
    {
      id: 'actiture',
      title: 'ACTITURE',
      color: '#F59E0B', 
      data: {
        aduana_despacho: '3161',
        aduana_salida: 'FR000740',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { codigo_documento: 'N380', tipo: 2 },
          { codigo_documento: 'N730', tipo: 4 },
          { codigo_documento: 'Y024', tipo: 3 },
          { codigo_documento: 'N003', tipo: 2 },
          { codigo_documento: '1217', tipo: 2 },
        ],
      },
    },
    {
      id: 'zanobeet',
      title: 'ZANOBEET',
      color: '#8B5CF6', 
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'CA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { codigo_documento: 'N380', tipo: 2 },
          { codigo_documento: 'N730', tipo: 4 },
          { codigo_documento: 'Y024', tipo: 3 },
          { codigo_documento: 'N003', tipo: 2 },
          { codigo_documento: '1217', tipo: 2 },
        ],
      },
    },
    {
      id: 'cebollas',
      title: 'CEBOLLAS',
      color: '#126FEF',
      data: {
        aduana_despacho: '3161',
        pais_exportacion: 'ES',
        provincia: 'NA',
        localizacion_mercancia: '3161K30007',
        documentos: [
          { codigo_documento: 'N380', tipo: 2 },
          { codigo_documento: 'N730', tipo: 4 },
          { codigo_documento: 'Y024', tipo: 3 },
          { codigo_documento: 'N003', tipo: 2 },
          { codigo_documento: '1217', tipo: 2 },
        ],
      },
    },
  ];
}

// Helper function to format field names
export const formatFieldName = (fieldName) => {
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// CRITICAL: Helper to extract fields from an object - include fields even if they have null values (for numeric fields like tipo) to allow fields with null defaults to be displayed and edited
const extractFields = (obj, excludeKeys = []) => {
  if (!obj || typeof obj !== 'object') return [];
  return Object.keys(obj)
    .filter(key => !excludeKeys.includes(key) && obj.hasOwnProperty(key))
    .map(key => ({ name: key, value: obj[key] }));
};

// Helper function to organize template fields hierarchically
export const organizeTemplateFields = (template) => {
  if (!template?.data || typeof template.data !== 'object') {
    return { declarationFields: [], documentFields: [], itemFields: [], embalajesFields: [] };
  }
  
  const { data } = template;
  const excludeFromDeclaration = ['documentos', 'partidas', 'embalajes', 'marcas'];
  
  // Declaration fields
  const declarationFields = extractFields(data, excludeFromDeclaration);
  
  // Item fields (from partidas object)
  const itemFields = (data.partidas && typeof data.partidas === 'object' && !Array.isArray(data.partidas))
    ? extractFields(data.partidas)
    : [];
  
  // Embalaje fields - collect unique fields from all embalajes
  const embalajesFields = [];
  if (Array.isArray(data.embalajes) && data.embalajes.length > 0) {
    const seenFields = new Set();
    data.embalajes.forEach(embalaje => {
      if (embalaje && typeof embalaje === 'object') {
        extractFields(embalaje).forEach(field => {
          if (!seenFields.has(field.name)) {
            seenFields.add(field.name);
            embalajesFields.push(field);
          }
        });
      }
    });
  }
  
  // Document fields
  const documentFields = [];
  if (Array.isArray(data.documentos) && data.documentos.length > 0) {
    data.documentos.forEach((doc, index) => {
      if (doc && typeof doc === 'object') {
        const docFields = extractFields(doc);
        if (docFields.length > 0) {
          documentFields.push({ docIndex: index, fields: docFields });
        }
      }
    });
  }
  
  return { declarationFields, documentFields, itemFields, embalajesFields };
};

// Generate a unique ID for new templates
export const generateNewTemplateId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `new-template-${timestamp}-${random}`;
};

// Generate a proper ID from title (for new templates)
export const generateTemplateId = (title) => {
  if (!title) return `template-${Date.now()}`;
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || `template-${Date.now()}`;
};

// Helper functions for marcas field (comma-separated format)
export const marcasToString = (marcasArray) => {
  if (!Array.isArray(marcasArray)) return '';
  return marcasArray
    .map(item => {
      if (typeof item === 'object' && item !== null && item.marca) {
        return item.marca;
      }
      return String(item);
    })
    .filter(Boolean)
    .join(', ');
};

export const stringToMarcas = (commaSeparatedString) => {
  if (!commaSeparatedString || typeof commaSeparatedString !== 'string') return [];
  return commaSeparatedString
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .map(marca => ({ marca }));
};

// Numeric field defaults - single source of truth
const NUMERIC_DEFAULTS = {
  'tipo': null,
  'completo': 1,
  'es_plantilla': 0,
  'flag_datos_seguridad': 0,
  'es_simplificado': 0,
  'es_recapitulativo': 0,
  'autorizacion_por_operacion': 0,
  'estado_pdc': 0,
  'estado_edi': 0,
  'estado_edi_canarias': 0,
  'version': 2,
  'tipo_autorizacion_cd': null,
  // NOTE: 'provincia_origen' is a 2-digit string code ("01".."52") and must NOT be converted to a number
  // (or leading zeros are lost and matching breaks). Keep it out of numeric conversions.
  'precio': null,
  'cantidad_euros': null,
  'valor_estadistico': null,
  'cantidad_divisas': null,
  'masa_bruta': null,
  'masa_neta': null,
  'cantidad': null,
};

// List of numeric field names that should be stored as numbers
export const NUMERIC_FIELDS = new Set(Object.keys(NUMERIC_DEFAULTS));

// Get default value for a numeric field
const getNumericDefault = (fieldName) => NUMERIC_DEFAULTS[fieldName] ?? null;

// Convert value to appropriate type based on field name
export const convertFieldValue = (fieldName, value) => {
  if (fieldName === 'marcas') {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string' && value.trim() === '') return [];
    return value; // Will be converted by stringToMarcas elsewhere
  }
  
  if (!NUMERIC_FIELDS.has(fieldName)) {
    return value; // Return as-is for non-numeric fields
  }
  
  // Handle empty/null values
  if (value === null || value === undefined || value === '') {
    return getNumericDefault(fieldName);
  }
  
  // Convert string to number
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return getNumericDefault(fieldName);
    const num = Number(trimmed);
    return (!isNaN(num) && isFinite(num)) ? num : null;
  }
  
  // Already a number
  if (typeof value === 'number') {
    return isFinite(value) ? value : null;
  }
  
  return null;
};

// Determine initial value based on field name
export const getInitialValue = (name) => {
  if (name === 'marcas') return [];
  return NUMERIC_DEFAULTS.hasOwnProperty(name) ? NUMERIC_DEFAULTS[name] : '';
};

// CRITICAL: Helper to collect field names from an object into a Set - only include fields that have actual values (not null/undefined) to allow fields like 'tipo' with null default to be added even if they exist with null value
const collectFieldNames = (obj) => {
  const fields = new Set();
  if (obj && typeof obj === 'object') {
    Object.keys(obj).forEach(key => {
      // Only add field if it has a non-null, non-undefined value (allows null-valued fields to be re-added as they're considered "not existing")
      if (obj[key] != null && obj[key] !== undefined) {
        fields.add(key);
      }
    });
  }
  return fields;
};

// Get existing field names from template
export const getExistingFields = (template) => {
  try {
    const data = template?.data;
    if (!data || typeof data !== 'object') {
      return {
        existingDeclarationFields: new Set(),
        existingDocumentFields: new Set(),
        existingItemFields: new Set(),
        existingEmbalajeFields: new Set()
      };
    }
    
    const excludeKeys = ['documentos', 'partidas', 'embalajes'];
    const declarationFields = new Set();
    Object.keys(data).forEach(key => {
      // CRITICAL: Only consider field as "existing" if it has a non-null, non-undefined value (allows fields like 'tipo' with null default to be added even if they exist with null value)
      if (!excludeKeys.includes(key) && data[key] != null && data[key] !== undefined) {
        declarationFields.add(key);
      }
    });
    
    return {
      existingDeclarationFields: declarationFields,
      existingDocumentFields: Array.isArray(data.documentos)
        ? data.documentos.reduce((acc, doc) => {
            collectFieldNames(doc).forEach(field => acc.add(field));
            return acc;
          }, new Set())
        : new Set(),
      existingItemFields: collectFieldNames(data.partidas),
      existingEmbalajeFields: Array.isArray(data.embalajes)
        ? data.embalajes.reduce((acc, emb) => {
            collectFieldNames(emb).forEach(field => acc.add(field));
            return acc;
          }, new Set())
        : new Set()
    };
  } catch (error) {
    console.error('Error getting existing fields:', error);
    return {
      existingDeclarationFields: new Set(),
      existingDocumentFields: new Set(),
      existingItemFields: new Set(),
      existingEmbalajeFields: new Set()
    };
  }
};

// Get existing field names for a specific documento index
export const getExistingFieldsForDocument = (template, documentIndex) => {
  try {
    const data = template?.data;
    const doc = Array.isArray(data?.documentos) && documentIndex != null
      ? data.documentos[documentIndex]
      : null;
    
    return { existingDocumentFields: collectFieldNames(doc) };
  } catch (error) {
    console.error('Error getting existing fields for document:', error);
    return { existingDocumentFields: new Set() };
  }
};

// Field definitions for search/selection
export const getAvailableFields = (isSpanish) => {
  return {
    declaration: [
      { name: 'tipo', label: !isSpanish ? 'Type' : 'Tipo' },
      { name: 'fecha_registro', label: !isSpanish ? 'Registration Date' : 'Fecha Registro' },
      { name: 'fecha_creacion', label: !isSpanish ? 'Creation Date' : 'Fecha Creación' },
      { name: 'fecha_operacion', label: !isSpanish ? 'Operation Date' : 'Fecha Operación' },
      { name: 'mrn', label: !isSpanish ? 'MRN' : 'MRN (Número de Referencia del Movimiento)' },
      { name: 'cliente_id', label: !isSpanish ? 'Client ID' : 'Cliente ID' },
      { name: 'agente_id', label: !isSpanish ? 'Agent ID' : 'Agente ID' },
      { name: 'remitente_id', label: !isSpanish ? 'Sender ID' : 'Remitente ID' },
      { name: 'clave_declarante', label: !isSpanish ? 'Declarant Key' : 'Clave Declarante' },
      { name: 'declaracion_1', label: !isSpanish ? 'Declaration 1' : 'Declaración 1' },
      { name: 'declaracion_2', label: !isSpanish ? 'Declaration 2' : 'Declaración 2' },
      { name: 'declaracion_3', label: !isSpanish ? 'Declaration 3' : 'Declaración 3' },
      { name: 'aduana_entrada', label: !isSpanish ? 'Entry Customs' : 'Aduana Entrada' },
      { name: 'aduana_salida', label: !isSpanish ? 'Exit Customs' : 'Aduana Salida' },
      { name: 'aduana_despacho', label: !isSpanish ? 'Clearance Customs' : 'Aduana Despacho' },
      { name: 'pais_destino', label: !isSpanish ? 'Destination Country' : 'País Destino' },
      { name: 'pais_exportacion', label: !isSpanish ? 'Export Country' : 'País Exportación' },
      { name: 'pais_transporte_frontera', label: !isSpanish ? 'Border Transport Country' : 'País Transporte Frontera' },
      { name: 'pais_ubicacion', label: !isSpanish ? 'Location Country' : 'País Ubicación' },
      { name: 'modo_transporte_frontera', label: !isSpanish ? 'Border Transport Mode' : 'Modo Transporte Frontera' },
      { name: 'modo_transporte_interior', label: !isSpanish ? 'Internal Transport Mode' : 'Modo Transporte Interior' },
      { name: 'medio_transporte_frontera', label: !isSpanish ? 'Transport Means (Border)' : 'Medio Transporte Frontera' },
      { name: 'identidad_transporte_frontera', label: !isSpanish ? 'Border Transport Identity' : 'Identidad Transporte Frontera' },
      { name: 'tipo_ubicacion', label: !isSpanish ? 'Location Type' : 'Tipo Ubicación' },
      { name: 'localizacion', label: !isSpanish ? 'Location Code' : 'Código de Localización' },
      { name: 'localizacion_mercancia', label: !isSpanish ? 'Goods Location' : 'Localización Mercancía' },
      { name: 'provincia', label: !isSpanish ? 'Province' : 'Provincia' },
      { name: 'codigo_un', label: !isSpanish ? 'UN Code (Delivery Place)' : 'Código UN (Lugar Entrega)' },
      { name: 'lugar_entrega', label: !isSpanish ? 'Delivery Place' : 'Lugar Entrega' },
      { name: 'tipo_autorizacion_cd', label: !isSpanish ? 'Type (49 Autorización C.D.)' : 'Tipo (49 Autorización C.D.)' },
      { name: 'identificacion_adicional', label: !isSpanish ? 'Additional Identification' : 'Identificación Adicional' },
      { name: 'divisa', label: !isSpanish ? 'Currency Code' : 'Código Divisa' },
      { name: 'cambio_divisa', label: !isSpanish ? 'Exchange Rate' : 'Cambio Divisa' },
      { name: 'divisa_iso', label: !isSpanish ? 'Currency ISO' : 'Divisa ISO' },
      { name: 'tiene_contenedores', label: !isSpanish ? 'Has Containers' : 'Tiene Contenedores' },
      { name: 'total_bultos', label: !isSpanish ? 'Total Packages' : 'Total Bultos' },
      { name: 'factura', label: !isSpanish ? 'Invoice' : 'Factura' },
      { name: 'fecha_factura', label: !isSpanish ? 'Invoice Date' : 'Fecha Factura' },
      { name: 'condiciones_entrega', label: !isSpanish ? 'Delivery Terms' : 'Condiciones Entrega' },
      { name: 'naturaleza_transaccion', label: !isSpanish ? 'Transaction Nature' : 'Naturaleza Transacción' },
      { name: 'observaciones', label: !isSpanish ? 'Observations' : 'Observaciones' },
      { name: 'e_mail', label: !isSpanish ? 'Contact Email' : 'Correo Electrónico de Contacto' },
      { name: 'contacto_telefono', label: !isSpanish ? 'Contact Phone' : 'Teléfono de Contacto' },
      { name: 'telefono', label: !isSpanish ? 'Phone' : 'Teléfono' },
      { name: 'autorizacion_por_operacion', label: !isSpanish ? 'Authorization per Operation' : 'Autorización por Operación' },
      { name: 'flag_datos_seguridad', label: !isSpanish ? 'Security Data (0/1)' : 'Datos de Seguridad (0/1)' },
      { name: 'es_simplificado', label: !isSpanish ? 'Simplified' : 'Simplificado' },
      { name: 'es_recapitulativo', label: !isSpanish ? 'Recapitulative' : 'Recapitulativo' },
      { name: 'estado_pdc', label: !isSpanish ? 'PDC Status' : 'Estado PDC' },
      { name: 'estado_edi', label: !isSpanish ? 'EDI Status' : 'Estado EDI' },
      { name: 'estado_edi_canarias', label: !isSpanish ? 'Canary Islands EDI Status' : 'Estado EDI Canarias' },
      { name: 'completo', label: !isSpanish ? 'Complete' : 'Completo' },
      { name: 'es_plantilla', label: !isSpanish ? 'Is Template' : '¿Es Plantilla?' },
      { name: 'regimen_solicitud', label: !isSpanish ? 'Request Regime' : 'Régimen Solicitud' },
    ],
    item: [
      { name: 'codigo_nc', label: !isSpanish ? 'NC Code' : 'Código NC' },
      { name: 'descripcion_mercancia', label: !isSpanish ? 'Goods Description' : 'Descripción Mercancía' },
      { name: 'masa_bruta', label: !isSpanish ? 'Gross Weight' : 'Masa Bruta' },
      { name: 'masa_neta', label: !isSpanish ? 'Net Weight' : 'Masa Neta' },
      { name: 'pais_origen', label: !isSpanish ? 'Country of Origin' : 'País Origen' },
      { name: 'provincia_origen', label: !isSpanish ? 'Province of Origin' : 'Provincia Origen' },
      { name: 'precio', label: !isSpanish ? 'Price' : 'Precio' },
      { name: 'cantidad_euros', label: !isSpanish ? 'Amount in Euros' : 'Cantidad Euros' },
      { name: 'valor_estadistico', label: !isSpanish ? 'Statistical Value' : 'Valor Estadístico' },
      { name: 'cantidad_divisas', label: !isSpanish ? 'Amount in Currency' : 'Cantidad Divisas' },
      { name: 'regimen_solicitado_precedente', label: !isSpanish ? 'Requested Previous Regime' : 'Régimen Solicitado Precedente' },
      { name: 'regimen_comunitario_nacional', label: !isSpanish ? 'Community/National Regime' : 'Régimen Comunitario Nacional' },
      { name: 'observaciones', label: !isSpanish ? 'Observations' : 'Observaciones' },
      { name: 'adiciones', label: !isSpanish ? 'Additions' : 'Adiciones' },
      { name: 'deducciones', label: !isSpanish ? 'Deductions' : 'Deducciones' },
      { name: 'observaciones_arancelarias', label: !isSpanish ? 'Tariff Observations' : 'Observaciones Arancelarias' },
      { name: 'observaciones_arancelarias_2', label: !isSpanish ? 'Tariff Observations 2' : 'Observaciones Arancelarias 2' },
      { name: 'deducciones_base_iva', label: !isSpanish ? 'IVA Base Deductions' : 'Deducciones Base IVA' },
    ],
    embalaje: [
      { name: 'tipo', label: !isSpanish ? 'Type' : 'Tipo' },
      { name: 'es_interno_o_externo', label: !isSpanish ? 'Internal or External' : 'Es Interno o Externo' },
      { name: 'cantidad', label: !isSpanish ? 'Quantity' : 'Cantidad' },
      { name: 'marcas', label: !isSpanish ? 'Marks' : 'Marcas' },
    ],
    document: [
      { name: 'tipo', label: !isSpanish ? 'Type' : 'Tipo' },
      { name: 'codigo_documento', label: !isSpanish ? 'Document Code' : 'Código de Documento' },
      { name: 'numero', label: !isSpanish ? 'Number' : 'Número' },
      { name: 'fecha', label: !isSpanish ? 'Date' : 'Fecha' },
    ],
  };
};

// Add fields to template data based on field level
export const addFieldsToTemplateData = (templateData, selectedFields, targetDocumentIndex = null) => {
  const newData = { ...(templateData || {}) };
  const documentFields = selectedFields.filter(f => f.fieldLevel === 'document');
  
  // Process document fields
  if (documentFields.length > 0) {
    const documentos = Array.isArray(newData.documentos) ? [...newData.documentos] : [];
    const doc = documentFields.reduce((acc, { fieldName }) => {
      acc[fieldName] = getInitialValue(fieldName);
      return acc;
    }, {});
    
    if (targetDocumentIndex != null && documentos[targetDocumentIndex]) {
      documentos[targetDocumentIndex] = { ...documentos[targetDocumentIndex], ...doc };
    } else {
      documentos.push(doc);
    }
    newData.documentos = documentos;
  }
  
  // Process other field levels
  selectedFields
    .filter(f => f.fieldLevel !== 'document')
    .forEach(({ fieldName, fieldLevel }) => {
      const value = getInitialValue(fieldName);
      
      if (fieldLevel === 'embalaje') {
        if (!Array.isArray(newData.embalajes) || newData.embalajes.length === 0) {
          newData.embalajes = [{ [fieldName]: value }];
        } else {
          newData.embalajes[0] = { ...newData.embalajes[0], [fieldName]: value };
        }
      } else if (fieldLevel === 'item') {
        if (!newData.partidas || Array.isArray(newData.partidas)) {
          newData.partidas = {};
        }
        newData.partidas[fieldName] = value;
      } else {
        newData[fieldName] = value;
      }
    });
  
  return newData;
};
